import WatsonApi from "../../../backends/WatsonApi";
import {SetStateAction, useContext, useEffect, useRef} from "react";
import {StoreContext} from "../../../stores/StoreLoader";
import NotificationManager from "../../../components/notifications/NotificationManager";
import {useMachine} from "@xstate/react";
import {FetchMachine} from "../../../components/machines/FetchMachine";
import {compressImage} from "../../../utils/DataUtilities";

type IFileUploadProps = {
    imageUrl: string,
    setImageUrl: SetStateAction<any>,
    imageTitle: string,
    setImageTitle: SetStateAction<any>,
    selectedThemeId?: string,
    hideFileName?: boolean,
    buttonClassName?: string,
    customButtonText?: [string, string],
}

export function getDisplayName (imageUrl) {
    if (/url\(/.test(imageUrl)) {
        // backgroundImage for floating blocks uses url("image.png") format
        const match = imageUrl.match(/url\("(.*)"\)/);
        if (match) {
            imageUrl = match[1];
        }
    }
    return imageUrl.split("/")[imageUrl.split("/").length - 1];
}

export function FileUpload(props: IFileUploadProps) {
    const {organizationStore} = useContext(StoreContext);
    let {imageUrl, imageTitle} = props;
    const inputRef = useRef<HTMLInputElement>(null);
    const [current, send] = useMachine(FetchMachine);

    async function sbSettings_imageReset() {
        send("FETCH");
        try {
            send("FULFILL");
            const client = await WatsonApi();
            const response = await client.apis.organizations.organizations_file_upload_delete({
                organization_pk: organizationStore.currentOrganization.id,
                attribute: imageTitle,
            })
            props.setImageUrl("");
            if (inputRef.current) {
                inputRef.current.value = '';
            }
            NotificationManager.success(`Success deleting ${imageTitle} image.`);
        } catch (e) {
            send("REJECT");
            NotificationManager.error(`Error deleting ${imageTitle} image`);
            console.error(String(e));
        }
    }

    async function sbSettings_fileInputChange(event) {
        event.stopPropagation();

        if (event.target.files.length === 0) return;

        send("FETCH");
        const files = event.target.files;

        if (files.length) {
            try {
                const file = await  compressImage(files[0], 1)
                const response = await organizationStore.imageUpload(imageTitle, file, file.name);
                send("FULFILL");
                props.setImageUrl(response.media_url);
                NotificationManager.success(`Success uploading ${imageTitle} image.`);
            } catch (e) {
                send("REJECT");
                NotificationManager.error(e.response.body, "Image Upload Error", 4000);
            }
        }
    }

    let displayName = "";
    if (imageUrl) {
        displayName = getDisplayName(imageUrl)
    }

    let label;
    if (current.value === "PENDING") {
        label = <label htmlFor={`input-${imageTitle}`} className={`btn btn-primary file-button disabled ${props.buttonClassName}`}>
            <span className="fas fa-spinner fa-pulse"/>
            <span style={{marginLeft: "10px"}}>Saving...</span>
        </label>
    } else {
        label = <label htmlFor={`input-${imageTitle}`} className={`btn btn-primary file-button ${props.buttonClassName}`}>
            <span>{imageUrl ?
                (props.customButtonText ? props.customButtonText[0] :
                    "replace image") : (props.customButtonText ? props.customButtonText[1] : "browse")}</span>
        </label>
    }

    return <>
        <input ref={inputRef} id={`input-${imageTitle}`} title={imageTitle} type="file" className="file-input hidden"
               name={`input-${imageTitle}`} onChange={sbSettings_fileInputChange}
               disabled={current.value === "PENDING"}/>
        {label}
        {props.hideFileName ||
            <>
                <span className={`form-control-static file-title-default ${imageUrl ? "hidden" : ""}`}>File not selected</span>
                <span className={`form-control-static file-title ${imageUrl ? "" : "hidden"}`}>
                {displayName}
                </span>
            </>
        }
        <a className={`file-delete ${imageUrl ? "" : "hidden"}`} onClick={sbSettings_imageReset}>
            &nbsp;<span className="fas fa-times-circle" aria-label="Delete existing image"/>
        </a>
    </>
}
